import type { ChartMedication as MedicationTabItem } from '../pages/clients/tabs/medicationTabTypes';

/**
 * Shared ownership / provenance / status labels for medication rows.
 *
 * Ownership (who manages the prescription) and provenance (how the record
 * entered the chart) are independent facts — e.g. "Our practice · Recorded
 * externally" and "Outside prescriber · Patient-reported" are both valid.
 * Neither label may erase the other.
 */

export type OwnershipKey = 'practice' | 'outside' | 'unconfirmed';

export function medicationOwnership(med: MedicationTabItem): OwnershipKey {
  if (med.managed_by === 'practice') return 'practice';
  if (med.managed_by === 'outside') return 'outside';
  return 'unconfirmed';
}

const OWNERSHIP_LABELS: Record<OwnershipKey, string> = {
  practice: 'Our practice',
  outside: 'Outside prescriber',
  unconfirmed: 'Ownership unconfirmed',
};

export function medicationOwnershipLabel(med: MedicationTabItem): string {
  return OWNERSHIP_LABELS[medicationOwnership(med)];
}

export type ProvenanceKey =
  | 'dosespot'
  | 'insurance'
  | 'patient_reported'
  | 'import'
  | 'intake'
  | 'pharmacy'
  | 'staff_recorded';

const PATIENT_REPORTED_SOURCES = new Set([
  'self_reported',
  'client_reported',
  'portal',
]);

/**
 * record_provenance is immutable — it is stamped once when the record enters
 * the chart and always wins. Legacy ``source`` and vendor linkage are only
 * fallbacks for rows written before the field existed; a vendor link
 * (vendor_prescription_id) is a separate fact about control, not provenance.
 */
const RECORD_PROVENANCE_MAP: Record<string, ProvenanceKey> = {
  dosespot_sync: 'dosespot',
  insurance_history: 'insurance',
  patient_report: 'patient_reported',
  import: 'import',
  intake: 'intake',
  manual: 'staff_recorded',
};

export function medicationProvenance(med: MedicationTabItem): ProvenanceKey {
  const provenance = med.record_provenance || '';
  if (provenance && provenance in RECORD_PROVENANCE_MAP) {
    return RECORD_PROVENANCE_MAP[provenance];
  }
  const source = med.source || '';
  if (source === 'insurance_reported' || source === 'insurance_history') {
    return 'insurance';
  }
  if (PATIENT_REPORTED_SOURCES.has(source)) return 'patient_reported';
  if (source === 'intake_form') return 'intake';
  if (source === 'pharmacy') return 'pharmacy';
  if (med.vendor_prescription_id) return 'dosespot';
  return 'staff_recorded';
}

const PROVENANCE_LABELS: Record<ProvenanceKey, string> = {
  dosespot: 'DoseSpot',
  insurance: 'Insurance-reported',
  patient_reported: 'Patient-reported',
  import: 'Imported',
  intake: 'Intake form',
  pharmacy: 'Pharmacy-reported',
  staff_recorded: 'Recorded by staff',
};

export function medicationProvenanceLabel(med: MedicationTabItem): string {
  return PROVENANCE_LABELS[medicationProvenance(med)];
}

export type StatusTone = 'active' | 'stopped' | 'warning' | 'unknown' | 'error';

export interface StatusChip {
  label: string;
  tone: StatusTone;
}

/**
 * Clinical medication status chip. Vendor transmission statuses
 * (pharmacy_verified, error, cancelled, deleted) are vendor details shown in
 * the expanded sync panel — they are never a medication status.
 */
export function medicationStatusChip(med: MedicationTabItem): StatusChip {
  const status = med.status || 'unknown';
  switch (status) {
    case 'active':
      return { label: 'Active', tone: 'active' };
    case 'discontinued':
      return { label: 'Discontinued', tone: 'stopped' };
    case 'held':
      return { label: 'On hold', tone: 'warning' };
    case 'reported_discontinued':
      return { label: 'Patient reports stopped', tone: 'warning' };
    case 'entered_in_error':
      return { label: 'Entered in error', tone: 'error' };
    case 'unknown':
    default:
      return { label: 'Status unknown', tone: 'unknown' };
  }
}

const STATUS_CHIP_CLASSES: Record<StatusTone, string> = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  stopped: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  unknown: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

export function statusChipClasses(tone: StatusTone): string {
  return STATUS_CHIP_CLASSES[tone];
}

/** Past-section status: chart status first, vendor rx_status labelled as vendor detail. */
export function pastMedicationStatusChip(med: MedicationTabItem): StatusChip {
  const rx = med.rx_status || '';
  if (med.status && med.status !== 'active') return medicationStatusChip(med);
  switch (rx) {
    case 'discontinued':
      return { label: 'Discontinued', tone: 'stopped' };
    case 'cancelled':
      return { label: 'Cancelled in DoseSpot', tone: 'stopped' };
    case 'deleted':
      return { label: 'Removed in DoseSpot', tone: 'stopped' };
    case 'error':
      return { label: 'DoseSpot error', tone: 'error' };
    default:
      return medicationStatusChip(med);
  }
}

/**
 * Collapsed-row transmission-failure alert. ``rx_status`` is the vendor
 * (DoseSpot) medication status synced onto the chart row; ``error`` means
 * the prescription transmission failed — the med stays clinically active,
 * but the row must not imply the prescription reached the pharmacy.
 */
export function transmissionFailureAlert(med: { rx_status?: string | null }): string | undefined {
  return med.rx_status === 'error' ? vendorRxStatusLabel('error') : undefined;
}

const VENDOR_RX_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending send',
  sent: 'Sent to pharmacy',
  pharmacy_verified: 'Pharmacy verified (vendor)',
  transmitted: 'Transmitted (vendor)',
  discontinued: 'Discontinued in DoseSpot',
  cancelled: 'Cancelled in DoseSpot',
  deleted: 'Removed in DoseSpot',
  error: 'DoseSpot send error',
};

/**
 * Label for a vendor (DoseSpot) prescription status. Vendor statuses describe
 * vendor-side transmission state — they are never a clinical medication
 * status, so always render them with vendor context.
 */
export function vendorRxStatusLabel(status?: string | null): string {
  if (!status) return 'DoseSpot status unknown';
  return VENDOR_RX_STATUS_LABELS[status] || status.replace(/_/g, ' ');
}

/**
 * True when a med carries payer-reported history still awaiting reconciliation.
 * A plain pending review flag is not enough — the row must actually carry
 * insurance-reported entries or be insurance-sourced itself.
 */
export function medicationNeedsInsuranceReconciliation(med: MedicationTabItem): boolean {
  const hasPendingHistory = med.insurance_history?.some(
    (h) => !h.disposition || h.disposition === 'pending_review',
  ) ?? false;
  return hasPendingHistory;
}
